import { searchCommands, shortcutCommands, type Command, type CommandEntry } from './commands';

export interface PaletteDom {
  readonly dialog: HTMLDialogElement;
  readonly input: HTMLInputElement;
  readonly list: HTMLUListElement;
}

const MODIFIER = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';

const keyCaps = (keys: readonly string[]): HTMLElement => {
  const caps = document.createElement('span');
  caps.className = 'tv-keys';
  caps.append(...keys.map((key) => Object.assign(document.createElement('kbd'), { textContent: key === '⌘' ? MODIFIER : key })));
  return caps;
};

export class Palette {
  private results: readonly CommandEntry[] = [];
  private active = 0;

  constructor(
    private readonly dom: PaletteDom,
    private readonly available: (command: Command) => boolean,
    private readonly run: (command: Command) => void,
  ) {
    dom.input.addEventListener('input', () => {
      this.active = 0;
      this.render();
    });
    dom.input.addEventListener('keydown', (event) => this.onKey(event));
  }

  open(): void {
    this.dom.input.value = '';
    this.active = 0;
    this.render();
    if (!this.dom.dialog.open) this.dom.dialog.showModal();
    this.dom.input.focus();
  }

  private onKey(event: KeyboardEvent): void {
    const moves: Readonly<Record<string, number>> = { ArrowDown: 1, ArrowUp: -1 };
    const move = moves[event.key];
    if (move !== undefined) {
      event.preventDefault();
      this.active = (this.active + move + this.results.length) % Math.max(1, this.results.length);
      this.render();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const chosen = this.results[this.active];
      if (chosen) this.choose(chosen);
    }
  }

  private choose(entry: CommandEntry): void {
    this.dom.dialog.close();
    this.run(entry.command);
  }

  private render(): void {
    this.results = searchCommands(this.dom.input.value, this.available);
    this.dom.list.replaceChildren(
      ...this.results.map((entry, index) => {
        const item = document.createElement('li');
        item.className = 'tv-palette-item';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', String(index === this.active));
        const title = Object.assign(document.createElement('span'), { className: 'tv-palette-title', textContent: entry.title });
        const group = Object.assign(document.createElement('span'), { className: 'tv-palette-group', textContent: entry.group });
        item.append(title, group, keyCaps(entry.keys));
        item.addEventListener('click', () => this.choose(entry));
        item.addEventListener('mousemove', () => {
          if (this.active === index) return;
          this.active = index;
          this.render();
        });
        return item;
      }),
    );
    if (this.results.length === 0) this.dom.list.append(Object.assign(document.createElement('li'), { className: 'tv-palette-empty', textContent: 'No matching command' }));
    this.dom.list.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }
}

export function renderShortcuts(list: HTMLElement): void {
  const rows = shortcutCommands().map((entry) => {
    const row = document.createElement('li');
    row.append(keyCaps(entry.keys), Object.assign(document.createElement('span'), { textContent: entry.title }));
    return row;
  });
  list.replaceChildren(...rows);
}
